/**
 * 五行人偶 sprite 库（5 属性 × 5 等级 = 25 个手画像素人偶）
 *
 * 设计参考图（5×5 矩阵）：
 *  - 列：金 / 木 / 水 / 火 / 土
 *  - 行：一等 / 二等 / 三等 / 四等 / 五等
 *  - 同列同色系（亮→暗）
 *  - 越高等级头冠越繁复、装饰越多
 *
 * 每个 sprite 32×32 canvas，PX=2 → 16×16 网格
 *  - 0-1 行：头冠
 *  - 2-4 行：头/脸
 *  - 5-10 行：躯干（衣袍）
 *  - 11-13 行：脚/底摆
 *  - 14-15 行：影子
 */

export type Element = "金" | "木" | "水" | "火" | "土";
export type Rank = 1 | 2 | 3 | 4 | 5;

export const ELEMENTS: Element[] = ["金", "木", "水", "火", "土"];

/** 每个元素的 5 个等级调色板（亮→暗） */
export interface ElementPalette {
  /** 头冠/装饰主色 */
  crown: string;
  /** 衣袍主色（袍身） */
  robe: string;
  /** 衣袍阴影 */
  robeShade: string;
  /** 衣袍高光 */
  robeLight: string;
  /** 脸/肤色 */
  skin: string;
  /** 眼睛 */
  eye: string;
  /** 装饰金/银 */
  trim: string;
}

export const PALETTES: Record<Element, [ElementPalette, ElementPalette, ElementPalette, ElementPalette, ElementPalette]> = {
  // 金 = 暖金黄（亮→暗）
  金: [
    { crown: "#fff8d0", robe: "#f0c860", robeShade: "#a87830", robeLight: "#ffe690", skin: "#fde4c0", eye: "#3a2810", trim: "#fff8d0" },
    { crown: "#f0d070", robe: "#d4a847", robeShade: "#8a6028", robeLight: "#f0c870", skin: "#fde4c0", eye: "#3a2810", trim: "#ffe680" },
    { crown: "#c89858", robe: "#b88838", robeShade: "#7a4f20", robeLight: "#d8a860", skin: "#fdd8b0", eye: "#3a2810", trim: "#e0c070" },
    { crown: "#a07840", robe: "#8a6028", robeShade: "#5a3818", robeLight: "#b88048", skin: "#fdd0a0", eye: "#2a1808", trim: "#c8a868" },
    { crown: "#6a4a22", robe: "#5a3818", robeShade: "#2a1808", robeLight: "#7a5028", skin: "#fdc890", eye: "#2a1808", trim: "#8a6840" },
  ],
  // 木 = 草绿（亮→暗）
  木: [
    { crown: "#e0f0a0", robe: "#5aaa3a", robeShade: "#2a6018", robeLight: "#88c860", skin: "#fde4c0", eye: "#1a2810", trim: "#e0f0a0" },
    { crown: "#a0c860", robe: "#3a8a28", robeShade: "#1a5010", robeLight: "#5aa848", skin: "#fde4c0", eye: "#1a2810", trim: "#c8e088" },
    { crown: "#68a040", robe: "#2a7020", robeShade: "#103808", robeLight: "#408838", skin: "#fdd8b0", eye: "#1a2810", trim: "#a0c870" },
    { crown: "#488028", robe: "#205818", robeShade: "#082008", robeLight: "#387028", skin: "#fdd0a0", eye: "#102008", trim: "#80a858" },
    { crown: "#2a4818", robe: "#143810", robeShade: "#041008", robeLight: "#1a4818", skin: "#fdc890", eye: "#102008", trim: "#487028" },
  ],
  // 水 = 蓝（亮→暗）
  水: [
    { crown: "#c8e8ff", robe: "#5a98d8", robeShade: "#1a4878", robeLight: "#88b8e8", skin: "#fde4c0", eye: "#0a1830", trim: "#e8f0ff" },
    { crown: "#88b8e8", robe: "#3878c0", robeShade: "#0a3868", robeLight: "#5a98d8", skin: "#fde4c0", eye: "#0a1830", trim: "#c8e0f8" },
    { crown: "#5888c0", robe: "#2860a0", robeShade: "#082848", robeLight: "#4880b8", skin: "#fdd8b0", eye: "#0a1830", trim: "#a0c0e8" },
    { crown: "#385878", robe: "#1a4068", robeShade: "#041828", robeLight: "#285888", skin: "#fdd0a0", eye: "#081020", trim: "#6090c0" },
    { crown: "#1a3050", robe: "#0a1830", robeShade: "#000810", robeLight: "#102848", skin: "#fdc890", eye: "#081020", trim: "#3060a0" },
  ],
  // 火 = 红橙（亮→暗）
  火: [
    { crown: "#ffe0a0", robe: "#e85838", robeShade: "#881818", robeLight: "#f08858", skin: "#fde4c0", eye: "#300808", trim: "#fff8d0" },
    { crown: "#f0a060", robe: "#cc3a20", robeShade: "#700808", robeLight: "#e85838", skin: "#fde4c0", eye: "#300808", trim: "#ffd890" },
    { crown: "#c86848", robe: "#a82810", robeShade: "#580808", robeLight: "#c84020", skin: "#fdd8b0", eye: "#300808", trim: "#e8a878" },
    { crown: "#984828", robe: "#801808", robeShade: "#380404", robeLight: "#a83018", skin: "#fdd0a0", eye: "#200404", trim: "#b87858" },
    { crown: "#5a2818", robe: "#480808", robeShade: "#180000", robeLight: "#681810", skin: "#fdc890", eye: "#200404", trim: "#7a4030" },
  ],
  // 土 = 棕褐（亮→暗）
  土: [
    { crown: "#e0c890", robe: "#a8804a", robeShade: "#5a3818", robeLight: "#c8a070", skin: "#fde4c0", eye: "#281808", trim: "#f0d8a0" },
    { crown: "#b89858", robe: "#886028", robeShade: "#3a2008", robeLight: "#a88048", skin: "#fde4c0", eye: "#281808", trim: "#d8b878" },
    { crown: "#886840", robe: "#684820", robeShade: "#281808", robeLight: "#886838", skin: "#fdd8b0", eye: "#281808", trim: "#b89868" },
    { crown: "#604828", robe: "#483018", robeShade: "#180c00", robeLight: "#685028", skin: "#fdd0a0", eye: "#181008", trim: "#907048" },
    { crown: "#382818", robe: "#281808", robeShade: "#080400", robeLight: "#382818", skin: "#fdc890", eye: "#181008", trim: "#5a4030" },
  ],
};

const PX = 2;
const SIZE = 32;
const GRID = SIZE / PX; // 16

/**
 * 绘制一个像素格
 */
function px(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string) {
  if (color === "" || color === "transparent") return;
  ctx.fillStyle = color;
  ctx.fillRect(x * PX, y * PX, w * PX, h * PX);
}

/**
 * 绘制"块像素"人偶到 canvas
 *  - el: 元素（决定色系）
 *  - rank: 1(最高) → 5(最低)
 *  - seed: 稳定哈希种子（用于细节差异）
 */
export function drawFigure(
  canvas: HTMLCanvasElement,
  el: Element,
  rank: Rank,
  seed: number
): void {
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, SIZE, SIZE);

  const pal = PALETTES[el][rank - 1];
  // 局部随机（用于衣摆花纹差异）
  const rng = mulberry32(seed ^ (rank * 1009) ^ el.charCodeAt(0));

  // 影子（底部 1 行）
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(SIZE / 2, SIZE - 2, 12, 2, 0, 0, Math.PI * 2);
  ctx.fill();

  // ============== 袍身（底摆最宽） ==============
  // 等级越高袍身越窄（身形修长），越低越宽松
  const robeWidth = 12 - Math.floor(rank * 0.6); // 12/11/11/10/10
  const robeHalf = Math.floor(robeWidth / 2);
  const robeCx = GRID / 2;

  // 袍身下摆（最宽处 y=11~13）
  for (let y = 11; y <= 13; y++) {
    const w = robeHalf + Math.floor((y - 11) * 0.8);
    px(ctx, robeCx - w, y, w * 2, 1, pal.robe);
    // 左右阴影
    if (w > 0) {
      px(ctx, robeCx - w, y, 1, 1, pal.robeShade);
      px(ctx, robeCx + w - 1, y, 1, 1, pal.robeShade);
    }
  }
  // 袍身中段 y=6~10
  for (let y = 6; y <= 10; y++) {
    const w = robeHalf - Math.floor((10 - y) * 0.4);
    px(ctx, robeCx - w, y, w * 2, 1, pal.robe);
    px(ctx, robeCx - w, y, 1, 1, pal.robeShade);
    px(ctx, robeCx + w - 1, y, 1, 1, pal.robeShade);
  }

  // 胸前高光（中间一条）
  px(ctx, robeCx - 1, 7, 2, 3, pal.robeLight);

  // 衣领（V 字形）
  px(ctx, robeCx - 1, 5, 2, 1, pal.trim);
  px(ctx, robeCx - 2, 6, 1, 1, pal.trim);
  px(ctx, robeCx + 1, 6, 1, 1, pal.trim);

  // 腰带（y=9 中间一行）
  px(ctx, robeCx - 2, 9, 4, 1, pal.trim);
  px(ctx, robeCx - 1, 9, 2, 1, pal.crown);

  // 袖摆（左/右，y=10 一格）
  px(ctx, robeCx - robeHalf - 1, 10, 1, 2, pal.robe);
  px(ctx, robeCx + robeHalf, 10, 1, 2, pal.robe);
  px(ctx, robeCx - robeHalf - 1, 11, 1, 1, pal.robeShade);
  px(ctx, robeCx + robeHalf, 11, 1, 1, pal.robeShade);

  // 衣摆花纹（随机小亮点，rank 越高越多）
  const decoCount = (6 - rank) * 3;
  for (let i = 0; i < decoCount; i++) {
    const dx = Math.floor(rng() * robeWidth) - robeHalf;
    const dy = 11 + Math.floor(rng() * 3);
    px(ctx, robeCx + dx, dy, 1, 1, pal.crown);
  }

  // ============== 头 ==============
  // 脸 y=4~5，2 格宽（×PX = 4 像素）
  px(ctx, robeCx - 1, 4, 2, 2, pal.skin);
  // 眼睛
  px(ctx, robeCx - 1, 4, 1, 1, pal.eye);
  px(ctx, robeCx, 4, 1, 1, pal.eye);

  // 头侧阴影
  px(ctx, robeCx - 2, 4, 1, 2, pal.robeShade);
  px(ctx, robeCx + 1, 4, 1, 2, pal.robeShade);

  // 耳朵/帽子左右挂饰
  if (rank <= 3) {
    px(ctx, robeCx - 2, 5, 1, 1, pal.trim);
    px(ctx, robeCx + 1, 5, 1, 1, pal.trim);
  }

  // ============== 头冠（按 rank 复杂） ==============
  drawCrown(ctx, robeCx, rank, pal, rng);

  // ============== 背后披风/肩饰（rank 越高越明显） ==============
  if (rank <= 2) {
    // 肩部横条
    px(ctx, robeCx - 3, 6, 6, 1, pal.trim);
    px(ctx, robeCx - 4, 7, 1, 1, pal.crown);
    px(ctx, robeCx + 3, 7, 1, 1, pal.crown);
  }
  if (rank === 1) {
    // 背后长披风
    px(ctx, robeCx - 4, 8, 1, 4, pal.crown);
    px(ctx, robeCx + 3, 8, 1, 4, pal.crown);
    // 披风阴影
    px(ctx, robeCx - 5, 9, 1, 3, pal.robeShade);
    px(ctx, robeCx + 4, 9, 1, 3, pal.robeShade);
  }
}

/**
 * 头冠绘制（5 个等级由繁到简）
 */
function drawCrown(
  ctx: CanvasRenderingContext2D,
  cx: number,
  rank: Rank,
  pal: ElementPalette,
  rng: () => number
) {
  switch (rank) {
    case 1: {
      // 一等：繁复羽冠 + 中央大宝石
      // 顶冠横向条
      px(ctx, cx - 3, 0, 7, 1, pal.crown);
      // 三柱羽冠（左/中/右）
      px(ctx, cx - 3, 0, 1, 1, pal.crown);
      px(ctx, cx - 4, 1, 1, 2, pal.crown);
      px(ctx, cx - 5, 1, 1, 1, pal.trim);

      px(ctx, cx - 1, -1, 1, 1, pal.crown); // 突出最顶
      px(ctx, cx, -1, 1, 1, pal.crown);
      px(ctx, cx + 1, 0, 1, 1, pal.crown);

      px(ctx, cx + 3, 0, 1, 1, pal.crown);
      px(ctx, cx + 4, 1, 1, 2, pal.crown);
      px(ctx, cx + 5, 1, 1, 1, pal.trim);

      // 中心宝石
      px(ctx, cx, 1, 1, 1, pal.trim);
      px(ctx, cx - 1, 2, 3, 1, pal.crown);
      // 宝石下高光
      px(ctx, cx - 2, 3, 5, 1, pal.crown);
      // 帽檐
      px(ctx, cx - 3, 3, 7, 1, pal.trim);
      // 左右垂饰
      px(ctx, cx - 3, 4, 1, 1, pal.trim);
      px(ctx, cx + 3, 4, 1, 1, pal.trim);
      break;
    }
    case 2: {
      // 二等：方冠 + 中央宝石
      px(ctx, cx - 2, 0, 5, 1, pal.crown);
      px(ctx, cx - 3, 1, 1, 2, pal.crown);
      px(ctx, cx + 3, 1, 1, 2, pal.crown);
      px(ctx, cx - 2, 1, 5, 2, pal.crown);
      // 中央宝石
      px(ctx, cx, 1, 1, 1, pal.trim);
      // 帽檐
      px(ctx, cx - 3, 3, 7, 1, pal.trim);
      break;
    }
    case 3: {
      // 三等：矮方冠
      px(ctx, cx - 2, 1, 5, 1, pal.crown);
      px(ctx, cx - 3, 2, 1, 1, pal.crown);
      px(ctx, cx + 3, 2, 1, 1, pal.crown);
      px(ctx, cx - 2, 2, 5, 1, pal.crown);
      // 中央小宝石
      px(ctx, cx, 1, 1, 1, pal.trim);
      // 帽檐
      px(ctx, cx - 3, 3, 7, 1, pal.trim);
      break;
    }
    case 4: {
      // 四等：圆顶小帽
      px(ctx, cx - 2, 1, 5, 1, pal.crown);
      px(ctx, cx - 2, 2, 5, 1, pal.crown);
      // 中央点
      px(ctx, cx, 1, 1, 1, pal.trim);
      break;
    }
    case 5: {
      // 五等：简单头巾
      px(ctx, cx - 2, 2, 5, 1, pal.crown);
      // 一条小阴影
      px(ctx, cx - 2, 3, 5, 1, pal.robeShade);
      break;
    }
  }

  // 通用：每个冠都有随机小亮片（仅 1-3 等）
  if (rank <= 3) {
    const sparkCount = 4 - rank;
    for (let i = 0; i < sparkCount; i++) {
      const sx = cx - 2 + Math.floor(rng() * 5);
      const sy = 1 + Math.floor(rng() * 2);
      px(ctx, sx, sy, 1, 1, pal.trim);
    }
  }
}

/**
 * mulberry32 简易确定性 PRNG
 */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}